import { Field, TextInput } from "@zapier/design-system";
import { MAX_TITLE_LENGTH } from "@zapier/canvas-constants";
import styles from "./StepTitleField.module.css";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useSetAtom } from "jotai";
import { takeSnapshotSetter } from "@zapier/canvas-state";
import useUpdateNode from "../../hooks/useUpdateNode";
import useMaybeSaveTitleOnUnmount from "../../hooks/useMaybeSaveTitleOnUnmount";

type Props = {
  label?: string;
  id: string;
};

export default function StepTitleField({ label, id }: Props) {
  const takeSnapshot = useSetAtom(takeSnapshotSetter);
  const updateNodeAndSave = useUpdateNode();
  const [newTitle, setNewTitle] = useState(label);

  useMaybeSaveTitleOnUnmount({ newTitle, label });

  const hasReachedMaxTitleLength = label?.length === MAX_TITLE_LENGTH;
  const characterCount =
    label?.length === 1
      ? `1/${MAX_TITLE_LENGTH} character`
      : `${label ? label.length : 0}/${MAX_TITLE_LENGTH} characters`;

  const handleTitleChange = useCallback(
    (evt: ChangeEvent<HTMLTextAreaElement>) => {
      const newLabel = evt.target.value;
      // We don't want to spam the BE on each keydown, so we update the node without saving
      setNewTitle(newLabel);
    },
    [],
  );

  /* This is to show the title change after StepTitle onBlur in the node.
   * The reason we don't update label in the node data directly is because it causes the input to re-render and lose focus. Cursor will keep jumping to the end of the input.
   * */
  useEffect(() => {
    if (label !== newTitle) {
      setNewTitle(label);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [label]);

  const saveTitle = useCallback(() => {
    const hasTitleChanged = newTitle !== label;
    if (hasTitleChanged) {
      takeSnapshot();
      updateNodeAndSave(
        {
          id,
          data: {
            label: newTitle,
          },
        },
        { shouldSave: true },
      );
    }
  }, [label, newTitle, updateNodeAndSave, takeSnapshot, id]);

  return (
    <Field
      error={
        hasReachedMaxTitleLength
          ? "You've reached the character limit."
          : undefined
      }
      isRequired={true}
      label="Title"
      renderInput={(inputProps) => (
        <TextInput
          {...inputProps}
          aria-required={true}
          placeholder="Describe what happens in this step"
          isMultiline={true}
          rows={3}
          value={newTitle}
          onChange={handleTitleChange}
          layout="block"
          onBlur={saveTitle}
          isErrored={hasReachedMaxTitleLength}
          maxLength={MAX_TITLE_LENGTH}
          appendContent={
            <div className={styles.appendContent}>
              <div className={styles.characterCount}>{characterCount}</div>
            </div>
          }
        />
      )}
    />
  );
}
